import { useGetFilterArrangementsQuery } from './selector/filterSelectorApi';

/** A row of the master filter-arrangement table (Flat-Bank / V-Bank). */
export interface IFilterArrangement {
  id: number;
  name?: string;
  value?: string;
  /** keeps the rows assignable to formKit's OptionRow. */
  [key: string]: unknown;
}

/**
 * The Arrangement options for a filter row, mirroring the server property that
 * produces them (`AHUFilter.arrangements`, models_component_filter.py:158-184):
 *
 *   no master filter chosen  -> the whole master table (Flat-Bank AND V-Bank)
 *   a master filter chosen   -> only the arrangements its CMS rows allow
 *
 * `model.arrangements` is a serializer field, so it is only ever as fresh as
 * the last GET/PATCH that carried it — and the catalogue Reset button is a
 * purely LOCAL write (catalogueField.ts `deselectModel`: it nulls
 * master_filter_id on the model and fires no request, and the rating dialog
 * suppresses the §49.2 autosave for its lifetime). So after a Reset the model
 * still carried the list the now-deselected filter had allowed: an existing
 * 12" filter, whose CMS rows offer Flat-Bank only, kept offering Flat-Bank
 * alone while a newly added filter — fetched with master_filter_id null —
 * offered both. Deriving the no-master case here is what makes the two agree.
 *
 * The master-filter case still comes from the model, and the rate round-trip
 * (`getFilterData`) now echoes the server's recomputed list back into it, so
 * changing the selection re-narrows the options in the same round trip that
 * recomputes the bank.
 */
export function useAllFilterArrangements(): IFilterArrangement[] {
  const { data } = useGetFilterArrangementsQuery();
  return data ?? [];
}

/**
 * Is `next` a real, different arrangement list? Guards the rate round-trip's
 * echo: a response without the key must not blank the model's list, and an
 * identical list must not count as a model change (a fresh array is `!==` the
 * old one, so every rate would otherwise read as one).
 */
export function arrangementsChanged(next: unknown, prev: unknown): boolean {
  if (!Array.isArray(next)) return false;
  const ids = (rows: unknown) =>
    Array.isArray(rows)
      ? (rows as IFilterArrangement[]).map((r) => r?.id).join(',')
      : '';
  return ids(next) !== ids(prev);
}

/** The option list to paint. See `useAllFilterArrangements` for the rule. */
export function pickFilterArrangements(
  masterFilterId: unknown,
  modelArrangements: unknown,
  allArrangements: IFilterArrangement[]
): IFilterArrangement[] {
  const stored = Array.isArray(modelArrangements)
    ? (modelArrangements as IFilterArrangement[])
    : [];
  if (masterFilterId) return stored;
  // Until the master table lands (or if the request failed), the model's own
  // list is still the better answer than an empty dropdown.
  return allArrangements.length ? allArrangements : stored;
}
